import axios, { type AxiosError } from 'axios';
import { shouldSkipAddressSaveToast } from '../utils/cash-out-address-error';
import { extractApiErrorMessage } from '../utils/collect-payment-guest';
import { getOrCreateDeviceToken } from '../utils/device-token';
import { toVendorNeutralMessage } from '../utils/vendor-neutral-message';

function isAnonymousPaymentInitialLoad(url: string, method?: string): boolean {
  if (method?.toUpperCase() !== 'GET' || !url.includes('anonymous-payment/')) {
    return false;
  }

  const pathOnly = url.split(/[?#]/)[0];
  return /anonymous-payment\/\d+$/.test(pathOnly);
}

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const axiosInstance = axios.create({
    baseURL: config.public.BASE_URL,
  });
  const nuxtApp = useNuxtApp();
  const cancelSource = axios.CancelToken.source();
  const { token, signOut } = useAuth();

  const skipErrorForEndpoints = [
    'unseen',
    'events',
    '/wallet/paypal/balance',
    'checkflo/check-pdf',
    'profile/resend-verification-email',
    'iam/get-role-permissions',
    // Loaded quietly in the background on the settings page. If it fails the limits card simply
    // does not appear, which is better than a red error over an otherwise working page.
    'direct-payment/transaction-limits',
  ];

  axiosInstance.interceptors.request.use((request) => {
    const requestUrl = request.url ?? '';
    const isAnonymousPaymentRequest = requestUrl.includes('anonymous-payment/');

    if (!isAnonymousPaymentRequest) {
      const authToken = localStorage.getItem('_snap_token') ?? token.value;
      if (authToken) {
        request.headers.Authorization = authToken.startsWith('Bearer') ? authToken : `Bearer ${authToken}`;
      }
    }

    request.headers.set('X-Snapr-Prefix', config.public.APP_PREFIX as string);
    request.headers.set('X-Device-Token', getOrCreateDeviceToken());

    const uid = localStorage.getItem('_profile_uid');
    if (uid) {
      request.headers.set('X-Snapr-Profile-Id', uid);
    }

    return request;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ message?: string | string[] | { message?: string } }>) => {
      if (error.response?.status === 401) {
        nuxtApp.$toastError(`You're token has expired. Please login again`);

        localStorage.removeItem('_snap_token');
        localStorage.removeItem('_profile_uid');
        localStorage.removeItem('_owner_id');
        localStorage.removeItem('_user_roles');
        localStorage.removeItem('_origin_app');
        localStorage.removeItem('snap_is_agent');

        signOut();

        return Promise.reject(error);
      }
      const responseUrl = error.request?.responseURL ?? error.config?.url ?? '';
      const pathOnly = responseUrl.split(/[?#]/)[0];
      const shouldErrorBeSkipped =
        skipErrorForEndpoints.some((endpoint) => pathOnly.includes(endpoint)) ||
        isAnonymousPaymentInitialLoad(responseUrl, error.config?.method);

      if (shouldErrorBeSkipped) {
        return Promise.reject(error);
      }

      if (error.response) {
        if (shouldSkipAddressSaveToast(pathOnly, error.response.data)) {
          return Promise.reject(error);
        }
        const message = error.response.data ? extractApiErrorMessage(error.response.data) : undefined;
        if (error.response.status >= 500 || !message) {
          nuxtApp.$toastError(`We're having some trouble`);
        } else {
          nuxtApp.$toastError(toVendorNeutralMessage(message));
        }
      }

      return Promise.reject(error);
    },
  );

  return {
    provide: {
      axios: axiosInstance,
      cancelSource,
    },
  };
});

/*

To use axios please setup BASE_URL at nuxt.config.ts like
Then you can get access to this variable like config.public.BASE_URL

runtimeConfig: {
    public: {
      BASE_URL: process.env.BASE_URL,
    },
  },

*/
